// src/store/slices/usersSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { userService, User, UserFormData, UserUpdateData } from '../../services/userService';

// Import UsersData type or define it here
interface UsersData {
  result: User[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  previousPage: number | null;
  nextPage: number | null;
}

interface UsersState {
  users: User[];
  pagination: {
    totalCount: number;
    currentPage: number;
    totalPages: number;
    previousPage: number | null;
    nextPage: number | null;
  } | null;
  isLoading: boolean;
  error: string | null; // This should only be for fetch/loading errors
}

const initialState: UsersState = {
  users: [],
  pagination: null,
  isLoading: false,
  error: null,
};

export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (page: number | undefined = undefined, { rejectWithValue }) => {
    try {
      const response = await userService.getAllUsers(page);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch users');
    }
  }
);

export const createUser = createAsyncThunk(
  'users/createUser',
  async (userData: UserFormData, { rejectWithValue }) => {
    try {
      const user = await userService.createUser(userData);
      return user;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create user');
    }
  }
);

export const updateUser = createAsyncThunk(
  'users/updateUser',
  async ({ id, userData }: { id: string; userData: UserUpdateData }, { rejectWithValue }) => {
    try {
      const user = await userService.updateUser(id, userData);
      return user;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update user');
    }
  }
);

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (id: string, { rejectWithValue }) => {
    try {
      await userService.deleteUser(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete user');
    }
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch users cases - these should set the error state
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null; // Clear any previous fetch errors
        
        // Handle different response formats
        if (Array.isArray(action.payload)) {
          state.users = action.payload;
          state.pagination = null;
        } else if (action.payload && 'result' in action.payload) {
          const data = action.payload as UsersData;
          state.users = data.result;
          state.pagination = {
            totalCount: data.totalCount,
            currentPage: data.currentPage,
            totalPages: data.totalPages,
            previousPage: data.previousPage,
            nextPage: data.nextPage
          };
        } else {
          state.users = [];
          state.pagination = null;
        }
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string; // Only set error for fetch failures
      })
      // Create user cases - these should NOT set the global error state
      .addCase(createUser.pending, (state) => {
        state.isLoading = true;
        // Don't clear the error state here - keep fetch errors visible
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users.push(action.payload);
        // Don't modify error state
      })
      .addCase(createUser.rejected, (state, action) => {
        state.isLoading = false;
        // Don't set error state - let the modal handle create errors
      })
      // Update user cases - these should NOT set the global error state
      .addCase(updateUser.pending, (state) => {
        state.isLoading = true;
        // Don't clear the error state here
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.isLoading = false;
        const { id, userData } = action.meta.arg;
        const index = state.users.findIndex(user => user._id === id);
        if (index !== -1) {
          state.users[index] = { ...state.users[index], ...userData };
        }
        // Don't modify error state
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.isLoading = false;
        // Don't set error state - let the modal handle update errors
      })
      // Delete user cases - these should NOT set the global error state
      .addCase(deleteUser.pending, (state) => {
        state.isLoading = true;
        // Don't clear the error state here
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = state.users.filter(user => user._id !== action.payload);
        // Don't modify error state
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.isLoading = false;
        // Don't set error state - let the modal/confirmation handle delete errors
      });
  },
});

export const { clearError } = usersSlice.actions;
export default usersSlice.reducer;